import React, { useCallback, useRef } from "react";
import { useDrop } from "react-dnd";
import { Button } from "@showitinc/showit-wc";
import cx from "classnames";
import AssetLibraryActions from "../../actions/AssetLibraryActions";
import Container from "./AssetLibraryContainer";
import Thumbnail from "./AssetThumbnail";
import Folder from "./AssetFolder";
import GalleryEditList from "./GalleryEditList";
import EmptyLibraryInstructions from "./EmptyLibraryInstructions";
import AssetActions from "../../actions/AssetActions";
import AssetLibraryStore from "../../stores/AssetLibraryStore";
import { generateDropSpec } from "../../utils/DnDUtils";
import { ASSET_ITEM } from "../../constants/DnDConstants";
import AppConfig from "../../config/app";
import uiPrefix from "../../utils/UiPrefixUtils";
import styles from "./AssetLibrary.module.css";

// Root folder component that accepts asset drops
const RootFolder = ({ selected, onClick, allowFolderDrops = false }) => {
  const drop = generateDropSpec({
    type: ASSET_ITEM,
    canDrop: () => allowFolderDrops,
    onDrop: (dragItem) => {
      // Get all selected assets from the store, or just the dragged asset if none are selected
      const selectedAssets = AssetLibraryStore.state.assets.filter((asset) => asset.selected);

      // If there are selected assets, move all of them. Otherwise, just move the dragged asset
      const assetsToMove = selectedAssets.length > 0 ? selectedAssets : [dragItem.asset];
      // Move the assets to root (null folder)
      AssetActions.moveToFolder(assetsToMove, null);
    },
  });

  const [{ isOver }, dropRef] = useDrop(drop);

  const topClasses = cx(styles.listed, styles["listed-page"], {
    [styles["active-label"]]: selected == null,
    listed: true,
    "listed-page": true,
    "active-label": selected == null,
    "drop-target": isOver,
  });
  const topFolderClasses = cx(styles["listed-page-icon"], {
    glyphicon: true,
    "glyphicon-folder_closed": selected != null,
    "glyphicon-folder_open": selected == null,
    "listed-page-icon": true,
  });

  return (
    <div
      className={topClasses}
      data-testid="asset-library-root-folder"
      onClick={onClick}
      ref={dropRef}
      style={{
        border: isOver ? "2px dashed var(--background-accent-primary)" : "none",
        backgroundColor: isOver ? "var(--background-hover-secondary)" : "transparent",
      }}
    >
      <span className={topFolderClasses} />
      <span>Uploads</span>
    </div>
  );
};

const AssetLibrary = ({
  onAssetSelect,
  onFolderDelete,
  galleryAssets,
  galleryItemsSelected,
  galleryExpanded,
  numSelected,
  galleryEditEnabled,
  assets,
  selectedFolder,
  folders,
  folderCreateEnabled,
  onAddFiles,
  demoMode,
  allowFolderDrops = false,
}) => {
  // Thumbnails observe this element to decide when to request their preview.
  const assetGridRef = useRef(null);

  const selectAsset = useCallback(
    (selection) => {
      if (onAssetSelect) {
        onAssetSelect(selection);
      }
    },
    [onAssetSelect]
  );

  const selectFolder = useCallback((folder) => {
    AssetLibraryActions.selectFolder(folder);
  }, []);

  const createFolder = useCallback(() => {
    AssetLibraryActions.showCreateFolder();
  }, []);

  const deleteFolder = useCallback(
    (folder) => {
      if (onFolderDelete) {
        onFolderDelete(folder);
      }
    },
    [onFolderDelete]
  );

  const renameFolder = useCallback((folder) => {
    AssetLibraryActions.showRenameFolder(folder);
  }, []);

  const addAssetToGallery = useCallback((data) => {
    AssetLibraryActions.addSelectedToList(data);
  }, []);

  const getGalleryList = useCallback(
    () => (
      <GalleryEditList
        galleryAssets={galleryAssets}
        galleryExpanded={galleryExpanded}
        galleryItemsSelected={galleryItemsSelected}
        numSelected={numSelected}
        galleryEditEnabled={galleryEditEnabled}
      />
    ),
    [galleryAssets, galleryItemsSelected, galleryExpanded, numSelected, galleryEditEnabled]
  );

  const getManageView = useCallback(() => {
    // Don't allow folder drops when in gallery edit mode
    const shouldAllowFolderDrops = allowFolderDrops && !galleryEditEnabled;

    let galleryList = null;
    let dropAction = null;

    if (galleryEditEnabled) {
      galleryList = getGalleryList();
      dropAction = addAssetToGallery;
    }

    const viewStyle = {
      display: "flex",
      minHeight: 0,
      justifyContent: "flex-start",
      alignItems: "stretch",
      alignContent: "stretch",
      width: "100%",
    };

    let gridContent = null;

    if (assets.length === 0 && !demoMode) {
      gridContent = <EmptyLibraryInstructions onAddFiles={onAddFiles} />;
    } else {
      gridContent = (
        <Container>
          {assets.map((asset, idx) => (
            <Thumbnail
              key={`asset-${asset.id || idx}`}
              index={idx}
              asset={asset}
              selected={asset.selected}
              onSelect={selectAsset}
              onDrop={dropAction}
              scrollRootRef={assetGridRef}
              draggable
            />
          ))}
        </Container>
      );
    }

    return (
      <div id="view_asset_library" className={styles["view-asset-library"]} style={viewStyle}>
        <div className={styles["asset-folder-column"]}>
          <RootFolder
            selected={selectedFolder}
            onClick={() => selectFolder(null)}
            allowFolderDrops={shouldAllowFolderDrops}
          />
          <div>
            {folders.map((folder) => {
              const active =
                selectedFolder != null && folder.folder_id === selectedFolder.folder_id;

              return (
                <Folder
                  key={folder.folder_id}
                  name={folder.name}
                  active={active}
                  enableContextMenu={!AppConfig.demoMode}
                  onClick={() => selectFolder(folder)}
                  onRename={() => renameFolder(folder)}
                  onDelete={() => deleteFolder(folder)}
                  folder={folder}
                  allowFolderDrops={shouldAllowFolderDrops}
                />
              );
            })}
          </div>
          {folderCreateEnabled && !AppConfig.demoMode && (
            <Button
              fullWidth
              variant="outline"
              iconPrefix="add"
              onClick={createFolder}
              data-e2e={uiPrefix("add-new-folder-btn")}
              className={`${uiPrefix("add-new-folder-btn")} listed add-page-button`}
            >
              Add new folder
            </Button>
          )}
        </div>
        <div className={styles["asset-grid-wrapper"]} ref={assetGridRef}>
          {gridContent}
        </div>
        {galleryList}
      </div>
    );
  }, [
    galleryEditEnabled,
    assets,
    selectedFolder,
    folders,
    folderCreateEnabled,
    onAddFiles,
    demoMode,
    allowFolderDrops,
    getGalleryList,
    addAssetToGallery,
    selectAsset,
    selectFolder,
    renameFolder,
    deleteFolder,
    createFolder,
  ]);

  const getGalleryOrganizeView = useCallback(() => getGalleryList(), [getGalleryList]);

  let view = null;

  if (galleryExpanded) {
    view = getGalleryOrganizeView();
  } else {
    view = getManageView();
  }

  return view;
};

export default AssetLibrary;
